import type { RouteLocationNormalized } from "vue-router";

/*
 * Ionic Vue drops a navigation that arrives while the page you are leaving has
 * not started its enter animation yet. Every entering page carries
 * `ion-page-invisible` until that animation starts, and
 * `IonRouterOutlet.handlePageTransition` returns early when the entering page
 * is visible and the leaving one is not. The first transition then finishes and
 * hides the page the URL points at.
 *
 * Tap a dashboard card and press back (or Home) right away: the URL is
 * /dashboard again but the card's page stays on screen. Home does nothing,
 * because the tab bar thinks you are on it, and back does nothing, because
 * there is no history left. Same for Settings or Beschikbaarheid, where even the
 * page's own back button only re-navigates to /dashboard.
 *
 * So a navigation first waits until the page it leaves has started entering.
 * That takes a few frames: tap to animation start measured at most 211 ms with
 * the CPU slowed down 6x. The cap only matters if the class never goes; then the
 * navigation goes ahead as it did before.
 */
const MAX_WAIT_MS = 500;

export function waitForPageEnter(from: RouteLocationNormalized): Promise<void> {
  // Ionic puts the page's view on its route record (`registerIonPage`); its root is the ion-page.
  const page = from.matched[from.matched.length - 1]?.instances.default?.$el;
  if (!(page instanceof HTMLElement) || !page.classList.contains("ion-page-invisible")) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const done = () => {
      observer.disconnect();
      clearTimeout(timer);
      resolve();
    };
    const observer = new MutationObserver(() => {
      if (!page.classList.contains("ion-page-invisible")) done();
    });
    observer.observe(page, { attributes: true, attributeFilter: ["class"] });
    const timer = setTimeout(done, MAX_WAIT_MS);
  });
}
